import { useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/useAuthStore';
import { ROUTES } from '../routes';
import SynapseDemoSection from '../components/landing/SynapseDemoSection';
import SocleDemoSection from '../components/landing/SocleDemoSection';
import {
  ChevronDown,
  ArrowUpRight,
  Brain,
  FileX,
  Unlink,
  Hourglass,
  BrainCircuit,
  LayoutDashboard,
  Rocket,
  Puzzle,
  Target,
  Scale,
  ShieldCheck,
  Server,
  Cpu,
  Layers,
  ArrowRight,
  Filter,
} from 'lucide-react';

// ─── Reveal CSS (injected via <style> in JSX) ──────────────────────────────

const REVEAL_CSS = `
.reveal {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.5, 0, 0, 1);
}
.reveal.active {
  opacity: 1;
  transform: translateY(0);
}
.reveal-delay-1 { transition-delay: 0.1s; }
.reveal-delay-2 { transition-delay: 0.2s; }
.reveal-delay-3 { transition-delay: 0.3s; }
.reveal-delay-4 { transition-delay: 0.4s; }
.reveal-delay-5 { transition-delay: 0.5s; }
.reveal-delay-6 { transition-delay: 0.6s; }
@keyframes discoverGlow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(139, 92, 246, 0); border-color: rgba(139, 92, 246, 0.35); }
  50% { box-shadow: 0 0 20px 2px rgba(139, 92, 246, 0.12); border-color: rgba(139, 92, 246, 0.55); }
}
`;

// ─── Style helpers ──────────────────────────────────────────────────────────

const gradientTextStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #ffffff 0%, #a3a3a3 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
};

const GLASS = 'bg-zinc-900/50 border border-zinc-800 rounded-2xl';

// ─── Constellation Canvas (Hero animation) ─────────────────────────────────

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
}

function ConstellationCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animRef = useRef<number>(0);

  const initParticles = useCallback((w: number, h: number) => {
    const count = Math.min(100, Math.max(80, Math.floor((w * h) / 10000)));
    const particles: Particle[] = [];
    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.6,
        vy: (Math.random() - 0.5) * 0.6,
        r: 1.5 + Math.random(),
      });
    }
    return particles;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      particlesRef.current = initParticles(rect.width, rect.height);
    };

    resize();
    window.addEventListener('resize', resize);

    const draw = () => {
      const rect = canvas.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      ctx.clearRect(0, 0, w, h);

      const particles = particlesRef.current;

      // Update positions
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
      }

      // Draw connections
      ctx.strokeStyle = 'rgba(167, 139, 250, 0.12)';
      ctx.lineWidth = 1;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 130) {
            ctx.globalAlpha = 1 - dist / 130;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw particles
      ctx.globalAlpha = 1;
      ctx.fillStyle = 'rgba(167, 139, 250, 0.5)';
      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [initParticles]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
}

// ─── Scroll reveal hook (IntersectionObserver) ──────────────────────────────

function useReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    const elements = document.querySelectorAll('.reveal');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

// ─── CTA Button (conditionnel connecté / non connecté) ──────────────────────

function CtaButton({ className = '', session }: { className?: string; session: boolean }) {
  const { t } = useTranslation();
  return session ? (
    <Link
      to="/dashboard"
      className={`inline-flex items-center gap-2 px-8 py-3 rounded-xl font-semibold text-sm transition-all ${className}`}
      aria-label={t('landing.cta_dashboard')}
    >
      {t('landing.cta_dashboard')}
      <ArrowUpRight className="w-4 h-4" />
    </Link>
  ) : (
    <Link
      to="/login"
      className={`inline-flex items-center gap-2 px-8 py-3 rounded-xl font-semibold text-sm transition-all ${className}`}
      aria-label={t('landing.cta_signup')}
    >
      {t('landing.cta_signup')}
      <ArrowUpRight className="w-4 h-4" />
    </Link>
  );
}

// ─── Pricing CTA (redirige vers /login ou /abonnement selon auth) ───────────

function PricingCta({ label, session }: { label: string; session: boolean }) {
  return (
    <Link
      to={session ? ROUTES.subscription : ROUTES.login}
      className="block w-full text-center px-6 py-2.5 rounded-xl font-semibold text-sm bg-violet-600 hover:bg-violet-500 text-white transition-colors"
    >
      {label}
    </Link>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export function LandingPage() {
  const { t } = useTranslation();
  const session = useAuthStore((state) => state.session);
  const hasSession = !!session;

  useReveal();

  const scrollToDiscover = () => {
    document.getElementById('demo-synapse')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div
      style={{ fontFamily: "'Sora', sans-serif" }}
      className="scroll-smooth bg-[#050505] text-zinc-100 min-h-screen"
    >
      <style>{REVEAL_CSS}</style>

      {/* ================================================================
          SECTION 1 - Hero / Couverture
          ================================================================ */}
      <section className="relative bg-[#050505] min-h-screen flex flex-col items-center justify-center px-6 text-center overflow-hidden">
        <ConstellationCanvas />
        <div className="relative max-w-3xl mx-auto reveal" style={{ zIndex: 10 }}>
          <span className="inline-block mb-6 px-4 py-1.5 text-xs tracking-widest uppercase text-indigo-400 border border-indigo-500/30 rounded-full">
            {t('landing.badge')}
          </span>

          <h1
            className="text-6xl md:text-8xl font-bold tracking-tight mb-4"
            style={gradientTextStyle}
          >
            VISUAL BRAIN
          </h1>

          <p className="text-xl md:text-2xl text-zinc-300 font-medium">{t('landing.subtitle_1')}</p>

          <p className="mt-6 text-xl md:text-2xl font-medium text-zinc-200">
            {t('landing.subtitle_2')}
          </p>

          <p className="mt-2 text-base md:text-lg font-normal text-zinc-400 max-w-xl mx-auto">
            {t('landing.description')}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <CtaButton
              session={hasSession}
              className="bg-white text-zinc-950 hover:bg-neutral-200"
            />
            <button
              onClick={scrollToDiscover}
              className="group inline-flex items-center gap-2.5 px-7 py-3 rounded-xl font-semibold text-sm text-violet-300 border border-violet-500/35 hover:border-violet-500/60 hover:text-violet-200 transition-all animate-[discoverGlow_3s_ease-in-out_infinite]"
            >
              <svg
                className="w-4 h-4 text-violet-400"
                viewBox="0 0 24 24"
                fill="currentColor"
                stroke="none"
              >
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
              {t('landing.cta_synapse_demo')}
              <ChevronDown className="w-3.5 h-3.5 text-violet-400/70 transition-transform group-hover:translate-y-0.5" />
            </button>
          </div>
        </div>
      </section>

      {/* ================================================================
          SECTION 3 - Le Probleme
          ================================================================ */}
      <section className="py-20 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 reveal">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={gradientTextStyle}>
              {t('landing.problem_intro')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 reveal">
            {[
              {
                icon: Brain,
                title: t('landing.problem_dispersion_title'),
                text: t('landing.problem_dispersion_desc'),
              },
              {
                icon: FileX,
                title: t('landing.problem_notes_title'),
                text: t('landing.problem_notes_desc'),
              },
              {
                icon: Unlink,
                title: t('landing.problem_connections_title'),
                text: t('landing.problem_connections_desc'),
              },
              {
                icon: Hourglass,
                title: t('landing.problem_friction_title'),
                text: t('landing.problem_friction_desc'),
              },
              {
                icon: BrainCircuit,
                title: t('landing.problem_overload_title'),
                text: t('landing.problem_overload_desc'),
              },
            ].map((card, i) => (
              <div
                key={card.title}
                className={`${GLASS} p-6 reveal-delay-${i + 1} ${i === 4 ? 'md:col-span-2' : ''}`}
              >
                <card.icon className="w-5 h-5 text-indigo-400 mb-3" />
                <h3 className="text-base font-semibold text-white mb-2">{card.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{card.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SocleDemoSection />

      <SynapseDemoSection />

      {/* ================================================================
          SECTION - Cas d'usage
          ================================================================ */}
      <section className="py-20 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 reveal">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={gradientTextStyle}>
              {t('landing.use_cases_heading')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 reveal">
            {[
              {
                icon: Rocket,
                title: t('landing.use_case_entrepreneur_title'),
                text: t('landing.use_case_entrepreneur_desc'),
              },
              {
                icon: Puzzle,
                title: t('landing.use_case_reasoning_title'),
                text: t('landing.use_case_reasoning_desc'),
              },
              {
                icon: Target,
                title: t('landing.use_case_lifeos_title'),
                text: t('landing.use_case_lifeos_desc'),
              },
              {
                icon: Scale,
                title: t('landing.use_case_decision_title'),
                text: t('landing.use_case_decision_desc'),
              },
              {
                icon: Brain,
                title: t('landing.use_case_neurodiv_title'),
                text: t('landing.use_case_neurodiv_desc'),
              },
            ].map((card, i) => (
              <div key={card.title} className={`${GLASS} p-6 reveal-delay-${i + 1}`}>
                <card.icon className="w-6 h-6 text-violet-400 mb-4" />
                <h3 className="text-base font-semibold text-white mb-2">{card.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{card.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================
          SECTION - Philosophie (fusionnee IA + UX)
          ================================================================ */}
      <section className="py-20 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 reveal">
            <span className="inline-block mb-6 px-3 py-1 text-xs font-medium tracking-widest uppercase text-indigo-400 border border-indigo-500/30 rounded-full">
              {t('landing.philosophy_badge')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={gradientTextStyle}>
              {t('landing.philosophy_heading')}
            </h2>
            <p className="text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto">
              {t('landing.philosophy_description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 reveal">
            {[
              {
                icon: ShieldCheck,
                title: t('landing.ai_validation_title'),
                text: t('landing.ai_validation_desc'),
              },
              {
                icon: Server,
                title: t('landing.ai_sovereign_title'),
                text: t('landing.ai_sovereign_desc'),
              },
              {
                icon: Cpu,
                title: t('landing.ai_service_title'),
                text: t('landing.ai_service_desc'),
              },
              {
                icon: Layers,
                title: t('landing.design_simplicity_title'),
                text: t('landing.design_simplicity_desc'),
              },
              {
                icon: LayoutDashboard,
                title: t('landing.design_spatial_title'),
                text: t('landing.design_spatial_desc'),
              },
              {
                icon: ArrowRight,
                title: t('landing.design_continuity_title'),
                text: t('landing.design_continuity_desc'),
              },
              {
                icon: Filter,
                title: t('landing.design_structure_title'),
                text: t('landing.design_structure_desc'),
              },
            ].map((item, i) => (
              <div key={item.title} className={`${GLASS} p-6 reveal-delay-${Math.min(i + 1, 6)}`}>
                <item.icon className="w-5 h-5 text-indigo-400 mb-3" />
                <h3 className="text-sm font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================
          SECTION 10 - Tarifs
          ================================================================ */}
      <section className="py-20 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 reveal">
            <span className="inline-block mb-6 px-3 py-1 text-xs font-medium tracking-wide text-violet-400 border border-violet-500/30 rounded-full">
              {t('landing.pricing_badge')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={gradientTextStyle}>
              {t('landing.pricing_heading')}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 reveal">
            {/* Free */}
            <div className={`${GLASS} p-6 flex flex-col reveal-delay-1`}>
              <h3 className="text-lg font-bold text-white mb-1">
                {t('landing.pricing_free_name')}
              </h3>
              <p className="text-3xl font-bold text-white mb-4">
                {t('landing.pricing_free_price_value')}
                <span className="text-sm font-normal text-zinc-500">
                  {t('landing.pricing_free_currency')}
                </span>
              </p>
              <ul className="space-y-2 text-sm text-zinc-400 mb-6 flex-1">
                <li>{t('landing.pricing_free_brain')}</li>
                <li>{t('landing.pricing_free_elements')}</li>
                <li>{t('landing.pricing_free_credits')}</li>
              </ul>
              <p className="text-xs text-zinc-500 italic mb-4">{t('landing.pricing_free_desc')}</p>
              <Link
                to={hasSession ? ROUTES.dashboard : ROUTES.login}
                className="block w-full text-center px-6 py-2.5 rounded-xl font-semibold text-sm border border-zinc-600 text-zinc-300 hover:border-zinc-500 hover:text-zinc-200 transition-colors"
              >
                {hasSession
                  ? t('landing.pricing_free_cta_dashboard')
                  : t('landing.pricing_free_cta_signup')}
              </Link>
            </div>

            {/* Pro - highlighted */}
            <div
              className={`${GLASS} p-6 flex flex-col border-violet-500/50 ring-1 ring-violet-500/20 reveal-delay-2`}
            >
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-bold text-white">{t('landing.pricing_pro_name')}</h3>
                <span className="px-2 py-0.5 text-[10px] font-medium tracking-wide uppercase text-violet-300 bg-violet-500/20 rounded-full">
                  {t('landing.pricing_pro_popular')}
                </span>
              </div>
              <p className="text-3xl font-bold text-white mb-4">
                {t('landing.pricing_pro_price_value')}
                <span className="text-sm font-normal text-zinc-500">
                  {t('landing.pricing_pro_currency')}
                </span>
              </p>
              <ul className="space-y-2 text-sm text-zinc-400 mb-6 flex-1">
                <li>{t('landing.pricing_pro_brain')}</li>
                <li>{t('landing.pricing_pro_elements')}</li>
                <li>{t('landing.pricing_pro_credits')}</li>
                <li>{t('landing.pricing_pro_views')}</li>
              </ul>
              <p className="text-xs text-zinc-500 italic mb-4">{t('landing.pricing_pro_desc')}</p>
              <PricingCta label={t('landing.pricing_pro_cta')} session={hasSession} />
            </div>

            {/* Max */}
            <div className={`${GLASS} p-6 flex flex-col reveal-delay-3`}>
              <h3 className="text-lg font-bold text-white mb-1">{t('landing.pricing_max_name')}</h3>
              <p className="text-3xl font-bold text-white mb-4">
                {t('landing.pricing_max_price_value')}
                <span className="text-sm font-normal text-zinc-500">
                  {t('landing.pricing_max_currency')}
                </span>
              </p>
              <ul className="space-y-2 text-sm text-zinc-400 mb-6 flex-1">
                <li>{t('landing.pricing_max_brain')}</li>
                <li>{t('landing.pricing_max_elements')}</li>
                <li>{t('landing.pricing_max_credits')}</li>
                <li>{t('landing.pricing_max_views')}</li>
              </ul>
              <p className="text-xs text-zinc-500 italic mb-4">{t('landing.pricing_max_desc')}</p>
              <PricingCta label={t('landing.pricing_max_cta')} session={hasSession} />
            </div>

            {/* Business */}
            <div className={`${GLASS} p-6 flex flex-col reveal-delay-4`}>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-bold text-white">
                  {t('landing.pricing_business_name')}
                </h3>
                <span className="px-2 py-0.5 text-[10px] font-medium tracking-wide uppercase text-zinc-400 border border-zinc-700 rounded-full">
                  {t('landing.pricing_business_soon')}
                </span>
              </div>
              <p className="text-3xl font-bold text-white mb-4">
                {t('landing.pricing_business_price_value')}
                <span className="text-sm font-normal text-zinc-500">
                  {t('landing.pricing_business_currency')}
                </span>
              </p>
              <ul className="space-y-2 text-sm text-zinc-400 mb-6 flex-1">
                <li>{t('landing.pricing_business_min_users')}</li>
                <li>{t('landing.pricing_business_collab')}</li>
              </ul>
              <p className="text-xs text-zinc-500 italic mb-4">
                {t('landing.pricing_business_desc')}
              </p>
              <a
                href="mailto:contact@visualbrain.io"
                className="block w-full text-center px-6 py-2.5 rounded-xl font-semibold text-sm border border-zinc-700 text-zinc-500 hover:border-zinc-600 hover:text-zinc-400 transition-colors"
              >
                {t('landing.pricing_business_cta')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          SECTION 12 - CTA final
          ================================================================ */}
      <section className="py-24 sm:py-40 px-6 bg-gradient-to-b from-zinc-900/50 to-[#050505]">
        <div className="max-w-3xl mx-auto text-center reveal">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={gradientTextStyle}>
            {t('landing.final_cta_heading')}
          </h2>

          <p className="text-zinc-400 text-base sm:text-lg leading-relaxed mb-4 max-w-2xl mx-auto">
            {t('landing.final_cta_desc')}
          </p>

          <p className="text-white font-semibold text-base sm:text-lg mb-10">
            {t('landing.final_cta_tagline')}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <CtaButton
              session={hasSession}
              className="bg-white text-zinc-950 hover:bg-neutral-200"
            />
            <Link
              to={ROUTES.roadmap}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-xl font-semibold text-sm text-zinc-400 border border-zinc-700 hover:border-zinc-500 hover:text-zinc-300 transition-all"
            >
              Voir la roadmap
            </Link>
          </div>
        </div>

        {/* Footer (préservé tel quel) */}
        <footer className="mt-20 border-t border-white/5 pt-8 pb-4">
          <div className="text-center space-y-3">
            <p
              className="text-xs text-neutral-500 font-medium"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              &copy; {new Date().getFullYear()} Visual Brain
            </p>
            <p className="text-xs text-neutral-500 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
              <Link
                to={ROUTES.mentionsLegales}
                className="hover:text-neutral-200 transition-colors"
              >
                {t('landing.footer_legal')}
              </Link>
              <span className="text-neutral-700">|</span>
              <Link to={ROUTES.cgu} className="hover:text-neutral-200 transition-colors">
                {t('landing.footer_cgu')}
              </Link>
              <span className="text-neutral-700">|</span>
              <Link to={ROUTES.cgv} className="hover:text-neutral-200 transition-colors">
                {t('landing.footer_cgv')}
              </Link>
              <span className="text-neutral-700">|</span>
              <Link
                to={ROUTES.confidentialite}
                className="hover:text-neutral-200 transition-colors"
              >
                {t('landing.footer_privacy')}
              </Link>
            </p>
            <p className="text-xs text-neutral-600">
              <a
                href="mailto:contact@visualbrain.io"
                className="hover:text-neutral-400 transition-colors"
              >
                contact@visualbrain.io
              </a>
            </p>
          </div>
        </footer>
      </section>
    </div>
  );
}

export default LandingPage;
